import { FiCpu, FiDownload, FiFileText, FiZap } from 'react-icons/fi';

export interface TemplateSummary {
  public_id: string;
  slug: string;
  name: string;
  description: string | null;
  category: string;
  complexity: 'beginner' | 'intermediate' | 'advanced';
  gpu_required: boolean;
  feature_tags: string[] | null;
  source_paper_count: number;
  clone_count: number;
  last_refreshed_at: string | null;
  is_new_this_week: boolean;
}

interface TemplateCardProps {
  template: TemplateSummary;
  onClone: (slug: string) => void | Promise<void>;
  cloning: boolean;
}

const COMPLEXITY_STYLES: Record<TemplateSummary['complexity'], string> = {
  beginner: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30',
  intermediate: 'bg-sky-500/10 text-sky-400 border-sky-500/30',
  advanced: 'bg-amber-500/10 text-amber-400 border-amber-500/30',
};

export default function TemplateCard({ template, onClone, cloning }: TemplateCardProps) {
  const complexityClass = COMPLEXITY_STYLES[template.complexity];
  const tags = (template.feature_tags ?? []).slice(0, 4);

  return (
    <div className="flex flex-col rounded-lg border border-neutral-800 bg-neutral-950 p-5 transition hover:border-neutral-700">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-semibold leading-tight text-white">
          {template.name}
        </h3>
        {template.is_new_this_week && (
          <span className="shrink-0 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-emerald-400">
            New
          </span>
        )}
      </div>

      {template.description && (
        <p className="mt-2 text-sm leading-relaxed text-neutral-400 line-clamp-3">
          {template.description}
        </p>
      )}

      <div className="mt-4 flex flex-wrap gap-1.5">
        <span className={`rounded border px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${complexityClass}`}>
          {template.complexity}
        </span>
        <span className="rounded border border-neutral-700 bg-neutral-800/60 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-neutral-400">
          {template.category.replace(/_/g, ' ')}
        </span>
        {template.gpu_required && (
          <span className="inline-flex items-center gap-1 rounded border border-purple-500/30 bg-purple-500/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-purple-400">
            <FiZap className="h-2.5 w-2.5" /> GPU
          </span>
        )}
      </div>

      {tags.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1">
          {tags.map((tag) => (
            <span
              key={tag}
              className="rounded-sm bg-neutral-800/50 px-1.5 py-0.5 text-[10px] text-neutral-400"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-4 text-xs text-neutral-500">
        <span className="inline-flex items-center gap-1">
          <FiFileText className="h-3 w-3" />
          {template.source_paper_count} paper{template.source_paper_count === 1 ? '' : 's'}
        </span>
        <span className="inline-flex items-center gap-1">
          <FiCpu className="h-3 w-3" />
          {template.clone_count} clone{template.clone_count === 1 ? '' : 's'}
        </span>
      </div>

      <div className="mt-5 flex-1" />

      <button
        type="button"
        onClick={() => onClone(template.slug)}
        disabled={cloning}
        className="mt-2 inline-flex items-center justify-center gap-2 rounded-md bg-white px-4 py-2 text-sm font-medium text-black transition hover:bg-neutral-200 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <FiDownload className="h-4 w-4" />
        {cloning ? 'Cloning...' : 'Clone to workspace'}
      </button>
    </div>
  );
}
